import React from 'react';
import { motion } from 'framer-motion';
import { Bug, Boxes, Users, MapPin } from 'lucide-react';

const PORTRAIT =
  'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/gmy2vu4q_personal%20photo.jpeg';

// Proficiency: 1 = Beginner · 2 = Intermediate · 3 = Advanced · 4 = Expert
const skills = [
  { brand: { icon: Bug }, label: 'UI / UX Testing & QA', sub: 'Game Tester', level: 2 },
  { brand: { letter: 'Z' }, label: 'ZBrush', level: 2 },
  { brand: { slug: 'unrealengine' }, label: 'Unreal Engine', sub: 'UI · Blueprints · Mechanics · Environment', level: 2 },
  { brand: { letter: 'Ai' }, label: 'Adobe Illustrator', level: 2 },
  { brand: { slug: 'unity' }, label: 'Unity & Unity AI', level: 1 },
  { brand: { image: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/r720w1t5_image.png' }, label: 'Substance 3D Painter', level: 1 },
];

const tools = [
  { brand: { slug: 'miro' }, label: 'Miro' },
  { brand: { image: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/o4vnxw9h_image.png' }, label: 'Microsoft Office' },
  { brand: { image: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/m70gphrx_image.png' }, label: 'Microsoft ClipChamp' },
  { brand: { letter: 'M' }, label: 'Mixamo' },
  { brand: { letter: 'C' }, label: 'Coggle' },
];

const facts = [
  { label: 'Based in', value: 'Hamburg, Germany', icon: MapPin },
  { label: 'Studying', value: 'MA Game Design · BSBI Berlin', icon: Boxes },
  { label: 'Currently', value: 'Community Game Tester · Keywords Studios', icon: Users },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

export default function About() {
  return (
    <section id="about" className="relative py-24 lg:py-32" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="lg:col-span-5 image-mask border border-white/5 rounded-lg group glow-card"
        >
          <img
            src={PORTRAIT}
            alt="Stephanos Papas — portrait"
            className="w-full h-[520px] lg:h-[640px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </motion.div>

        <div className="lg:col-span-7">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#e58a59]">
              About
            </span>
            <div className="flex-1 h-px bg-white/10" />
          </motion.div>

          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="font-display font-light text-white text-4xl lg:text-6xl leading-[1.1] mb-8"
            data-testid="about-heading"
          >
            A <span className="italic text-[#e58a59]">player-first</span> designer
            crossing over from live ops into game design.
          </motion.h2>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="text-zinc-400 text-base lg:text-lg leading-relaxed mb-6"
          >
            I’m <span className="text-white font-semibold">Stephanos Papas</span> —
            an aspiring game designer based in Hamburg, currently completing my
            MA in Game Design at the Berlin School of Business &amp; Innovation.
            I sculpt characters in ZBrush, prototype mechanics in Unreal and
            Unity, and ship board-game-style design docs in Illustrator.
          </motion.p>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="text-zinc-400 text-base lg:text-lg leading-relaxed mb-10"
          >
            Before design school I coordinated live operations at Hermes
            Airports (200+ flights a day) and ran Discord communities for 100+
            players — experience that taught me to read systems under pressure
            and design with the player firmly at the centre.
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {facts.map((f) => (
              <div
                key={f.label}
                className="px-4 py-3 rounded-lg border border-white/10 bg-[#121214]"
                data-testid={`about-fact-${f.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
                  <f.icon size={12} className="text-[#e58a59]" />
                  {f.label}
                </div>
                <div className="text-sm text-white leading-snug">{f.value}</div>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-8 space-y-8">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Software &amp; Engines
                </div>
                <ProficiencyLegend />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {skills.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.04 }}
                    className="flex items-start gap-3 px-4 py-3 rounded-lg border border-white/10 bg-[#121214] hover:border-white/30 transition-colors"
                    data-testid={`skill-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <BrandIcon brand={s.brand} size={20} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 font-medium leading-tight">{s.label}</div>
                      {s.sub && (
                        <div className="text-[10px] text-zinc-500 mt-1 leading-snug">{s.sub}</div>
                      )}
                    </div>
                    <ProficiencyDots level={s.level} />
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                Tools Knowledge
              </div>
              <div className="flex flex-wrap gap-2">
                {tools.map((t) => (
                  <div
                    key={t.label}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-[#121214] hover:border-white/30 transition-colors"
                    data-testid={`tool-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <BrandIcon brand={t.brand} size={14} />
                    <span className="text-xs text-zinc-200">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const LEVEL_LABELS = {
  1: 'Beginner',
  2: 'Intermediate',
  3: 'Advanced',
  4: 'Expert',
};

function ProficiencyDots({ level = 1 }) {
  return (
    <div
      className="flex items-center gap-1 mt-1 shrink-0"
      title={LEVEL_LABELS[level]}
      aria-label={`Proficiency: ${LEVEL_LABELS[level]}`}
    >
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`block w-1.5 h-3 rounded-sm transition-colors ${
            i <= level ? 'bg-[#e58a59]' : 'bg-white/10'
          }`}
        />
      ))}
    </div>
  );
}

function ProficiencyLegend() {
  return (
    <div className="hidden md:flex items-center gap-3 text-[9px] uppercase tracking-[0.2em] text-zinc-500">
      <LegendItem level={1} label="Beginner" />
      <LegendItem level={2} label="Intermediate" />
      <LegendItem level={3} label="Advanced" />
      <LegendItem level={4} label="Expert" />
    </div>
  );
}

function LegendItem({ level, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <ProficiencyDots level={level} />
      <span>{label}</span>
    </span>
  );
}

function BrandIcon({ brand, size = 18, className = '' }) {
  if (!brand) return null;
  // Lucide icon fallback (for non-brand things like QA)
  if (brand.icon) {
    const Icon = brand.icon;
    return <Icon size={size} className={`text-[#e58a59] shrink-0 ${className}`} />;
  }
  // Direct image (uploaded brand logo) — tinted to accent for visual consistency with the rest of the icon set
  if (brand.image) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-md shrink-0 overflow-hidden ${className}`}
        style={{ width: size, height: size }}
      >
        <img
          src={brand.image}
          alt=""
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: 'grayscale(1) sepia(0.9) saturate(3.5) hue-rotate(-18deg) brightness(1.05) contrast(1.05)',
          }}
        />
      </span>
    );
  }
  // Simple-icons CDN logo (dark theme brand color forced to accent orange)
  if (brand.slug) {
    return (
      <img
        src={`https://cdn.simpleicons.org/${brand.slug}/e58a59`}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className={`shrink-0 ${className}`}
      />
    );
  }
  // Letter badge fallback for brands without a simple-icons entry
  return (
    <span
      className={`inline-flex items-center justify-center rounded border border-[#e58a59]/50 bg-[#e58a59]/10 text-[#e58a59] font-bold shrink-0 px-1 ${className}`}
      style={{
        minWidth: size,
        height: size,
        fontSize: Math.max(8, Math.round(size * (brand.letter && brand.letter.length > 1 ? 0.5 : 0.55))),
      }}
      aria-hidden="true"
    >
      {brand.letter}
    </span>
  );
}
